import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  AlertColor,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { SetStateAction, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  BentoBrand,
  BentoContact,
  BentoContactArchiveReason,
  BentoContactResponse,
} from "schemas/dashboard";
import { Message, MessageAttachment } from "schemas/email";
import { CustomEvent } from "schemas/functions";

import Drawer from "components/Drawer";
import EmailThread from "components/EmailThread";
import BrandSearchAutoComplete from "features/Admin/AdminSharedComponents/BrandSearchAutoComplete";
import { fetcherAuth } from "utils/api";
import { openBase64DataInTab } from "utils/files";
import { camelToTitleCase, snakeToCamel } from "utils/string";
import { makeDeepCopy } from "utils/updateLocalState";

import styles from "./styles";

interface EditContactProps {
  setAlert: (
    customMessage: string | JSX.Element,
    customType: AlertColor,
  ) => void;
  setLoading: React.Dispatch<SetStateAction<boolean>>;
  refetchContacts: () => void;
}

const ADMIN_ARCHIVE_REASONS = [
  BentoContactArchiveReason.contact_requested,
  BentoContactArchiveReason.invalid_format,
  BentoContactArchiveReason.low_hunter_score,
  BentoContactArchiveReason.bounced,
  BentoContactArchiveReason.consecutive_blocks,
  BentoContactArchiveReason.spam,
];

const EditContact = ({
  setAlert,
  refetchContacts,
  setLoading,
}: EditContactProps) => {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();

  const contactId = Number(id);

  const [contact, setContact] = useState<BentoContact | undefined>();
  const [messages, setMessages] = useState<Message[]>([]);
  const [archiveReasonError, setArchiveReasonError] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [bentoContactResponse, setBentoContactResponse] = useState<
    BentoContactResponse | undefined
  >();

  const fetchContact = async () => {
    setLoading(true);
    try {
      const { contact } = await fetcherAuth(
        getToken,
        `/api/admin/bento-contacts/${contactId}`,
        "GET",
      );
      setContact(contact);
    } catch (error) {
      setAlert(`An error occurred ${error}`, "warning");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContact();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const handleClose = () => {
    navigate(`/admin/contacts`, { replace: true });
  };

  const fetchResponse = async (responseId: number) => {
    setLoading(true);
    try {
      let url = `/api/admin/bento-contacts/responses/${responseId}`;
      const { bentoContactResponse, messages } = await fetcherAuth(
        getToken,
        url,
      );
      setMessages(messages);
      setBentoContactResponse(bentoContactResponse);
    } catch (error) {
      setAlert(`An error occurred ${error}`, "warning");
    } finally {
      setLoading(false);
    }
  };

  const openAttachment = async (
    messageId: string,
    attachment: MessageAttachment,
  ) => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/user-integrations/${bentoContactResponse?.userIntegrationId}/messages/${messageId}/attachments/${attachment.attachment_id}`,
        "GET",
      );
      openBase64DataInTab(res.attachment.data, attachment.mime_type);
    } catch (error) {
      setAlert(`An error occurred ${error}`, "warning");
    }
  };

  const onBrandChange = async (
    event: CustomEvent,
    newValue: BentoBrand | null,
  ) => {
    setContact((prev) => {
      if (prev) {
        const copy: BentoContact = makeDeepCopy(prev);
        copy.bentoBrand = newValue || undefined;
        copy.bentoBrandId = newValue?.id;
        return copy;
      }
    });
  };

  const onChange = (event: CustomEvent) => {
    const { name, value, checked } = event.target;

    if (name === "archiveReason") {
      setArchiveReasonError(false);
    }

    setContact((prev) => {
      if (prev) {
        const copy = makeDeepCopy(prev);
        if (name === "isArchived") {
          copy[name] = checked;
        } else if (name === "archiveReason" && value === "None") {
          copy[name] = null;
        } else {
          copy[name] = value;
        }
        return copy;
      }
    });
  };

  const saveChanges = async () => {
    setSaveLoading(true);
    try {
      let url = `/api/admin/bento-contacts/${contact?.id}`;
      const { contact: updatedContact } = await fetcherAuth(
        getToken,
        url,
        "PUT",
        {},
        {
          ...contact,
        },
      );
      setAlert("Saved", "success");
      setContact(updatedContact);
    } catch (error) {
      if (error.message.toLowerCase().includes("archive reason")) {
        setArchiveReasonError(true);
      }
      setAlert(`An error occurred ${error}`, "warning");
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Drawer
      open={contactId > 0}
      width={{ xs: "auto", md: 800, lg: 1000 }}
      onClose={handleClose}
    >
      <DialogTitle sx={styles.dialogTitleText}>
        <Grid container alignItems="center" justifyContent={"space-between"}>
          <Button
            color="secondary"
            onClick={handleClose}
            sx={styles.actionButton}
            size="small"
          >
            Close
          </Button>

          <Box>
            <Typography component="span" noWrap sx={styles.brandName}>
              {contact?.name}
            </Typography>
          </Box>
          <LoadingButton
            variant="contained"
            sx={styles.actionButton}
            onClick={saveChanges}
            loading={saveLoading}
            disableElevation
            size="small"
          >
            Save
          </LoadingButton>
        </Grid>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Grid container rowSpacing={3} columnSpacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <BrandSearchAutoComplete
              setAlert={setAlert}
              value={contact?.bentoBrand || null}
              onBrandChange={onBrandChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Name"
              onChange={onChange}
              value={contact?.name || ""}
              name="name"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              onChange={onChange}
              value={contact?.email || ""}
              name="email"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title"
              onChange={onChange}
              value={contact?.title || ""}
              name="title"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Email Score"
              type="number"
              onChange={onChange}
              value={contact?.score || ""}
              name="score"
            />
          </Grid>
          <Grid item xs="auto">
            <FormControlLabel
              control={
                <Switch
                  name="isArchived"
                  onChange={onChange}
                  checked={contact?.isArchived}
                />
              }
              label="Is Archived"
            />
          </Grid>
          <Grid item xs={3}>
            <Typography sx={{ mb: 0.5, mt: -2, fontSize: "12px" }}>
              Archive Reason
            </Typography>
            <Select
              onChange={onChange}
              error={archiveReasonError}
              name="archiveReason"
              value={contact?.archiveReason || "None"}
              size="small"
            >
              <MenuItem value="None">
                <em>None</em>
              </MenuItem>
              {ADMIN_ARCHIVE_REASONS?.map((item, index) => (
                <MenuItem key={index} value={item}>
                  {camelToTitleCase(snakeToCamel(item))}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
        <Typography variant="h5" sx={{ mt: 3 }}>
          Responses for this contact
        </Typography>
        <Grid container sx={styles.emailRoot}>
          <Grid item container xs={4} sx={styles.wrapper}>
            {contact?.bentoContactResponses?.length === 0 && (
              <Typography variant="caption">No responses found</Typography>
            )}
            {contact?.bentoContactResponses?.map((res) => (
              <Grid key={res.id} item sx={{ mb: 2, mr: 2, p: 1 }} xs={12}>
                <Card>
                  <CardContent>
                    <Typography>
                      <strong>{res.userIntegration?.email}</strong>
                    </Typography>
                    <Typography>
                      Response types: <i>{res.responseTypes?.join(", ")}</i>
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button onClick={() => fetchResponse(res.id)}>View</Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Grid item xs={8} sx={styles.wrapper}>
            <EmailThread messages={messages} openAttachment={openAttachment} />
          </Grid>
        </Grid>
      </DialogContent>
    </Drawer>
  );
};

export default EditContact;
